import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { SEO } from "@/components/SEO";
import { locations } from "@/data/locationData";

const Home = lazy(() => import("@/pages/Home").then(m => ({ default: m.Home })));
const Pricing = lazy(() => import("@/pages/Pricing").then(m => ({ default: m.Pricing })));
const Services = lazy(() => import("@/pages/Services").then(m => ({ default: m.Services })));
const Work = lazy(() => import("@/pages/Work").then(m => ({ default: m.Work })));
const About = lazy(() => import("@/pages/About").then(m => ({ default: m.About })));
const Contact = lazy(() => import("@/pages/Contact").then(m => ({ default: m.Contact })));
const Blog = lazy(() => import("@/pages/Blog").then(m => ({ default: m.Blog })));
const BlogPost = lazy(() => import("@/pages/BlogPost").then(m => ({ default: m.BlogPost })));
const Privacy = lazy(() => import("@/pages/Privacy").then(m => ({ default: m.Privacy })));
const Terms = lazy(() => import("@/pages/Terms").then(m => ({ default: m.Terms })));
const LocationPage = lazy(() => import("@/pages/LocationPage").then(m => ({ default: m.LocationPage })));
const Process = lazy(() => import("@/pages/Process").then(m => ({ default: m.Process })));
const VsAgencies = lazy(() => import("@/pages/VsAgencies").then(m => ({ default: m.VsAgencies })));
const VsDIY = lazy(() => import("@/pages/VsDIY").then(m => ({ default: m.VsDIY })));
const VsUpwork = lazy(() => import("@/pages/VsUpwork").then(m => ({ default: m.VsUpwork })));
const DigitalMarketing = lazy(() => import("@/pages/DigitalMarketing").then(m => ({ default: m.DigitalMarketing })));
const SEOServices = lazy(() => import("@/pages/SEOServices").then(m => ({ default: m.SEOServices })));
const GoogleAds = lazy(() => import("@/pages/GoogleAds").then(m => ({ default: m.GoogleAds })));
const SocialMediaMarketing = lazy(() => import("@/pages/SocialMediaMarketing").then(m => ({ default: m.SocialMediaMarketing })));
const ContentMarketing = lazy(() => import("@/pages/ContentMarketing").then(m => ({ default: m.ContentMarketing })));
const LawFirmMarketing = lazy(() => import("@/pages/LawFirmMarketing").then(m => ({ default: m.LawFirmMarketing })));
const MedicalPracticeMarketing = lazy(() => import("@/pages/MedicalPracticeMarketing").then(m => ({ default: m.MedicalPracticeMarketing })));
const ConstructionWebDesign = lazy(() => import("@/pages/ConstructionWebDesign").then(m => ({ default: m.ConstructionWebDesign })));
const DentalWebDesign = lazy(() => import("@/pages/DentalWebDesign").then(m => ({ default: m.DentalWebDesign })));
const ReviewRequest = lazy(() => import("@/pages/ReviewRequest").then(m => ({ default: m.ReviewRequest })));
const HomeServices = lazy(() => import("@/pages/HomeServices").then(m => ({ default: m.HomeServices })));
const RoofingWebDesign = lazy(() => import("@/pages/home-services/RoofingWebDesign").then(m => ({ default: m.RoofingWebDesign })));
const PlumberWebDesign = lazy(() => import("@/pages/home-services/PlumberWebDesign").then(m => ({ default: m.PlumberWebDesign })));
const HvacWebDesign = lazy(() => import("@/pages/home-services/HvacWebDesign").then(m => ({ default: m.HvacWebDesign })));
const ElectricianWebDesign = lazy(() => import("@/pages/home-services/ElectricianWebDesign").then(m => ({ default: m.ElectricianWebDesign })));
const LandscapingWebDesign = lazy(() => import("@/pages/home-services/LandscapingWebDesign").then(m => ({ default: m.LandscapingWebDesign })));

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <SEO />
        <Navbar />
        <main>
          <Suspense fallback={<div className="min-h-screen bg-theme-dark" />}>
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/services" element={<Services />} />
            <Route path="/work" element={<Work />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/process" element={<Process />} />
            <Route path="/vs-agencies" element={<VsAgencies />} />
            <Route path="/vs-diy-builders" element={<VsDIY />} />
            <Route path="/vs-upwork" element={<VsUpwork />} />
            <Route path="/digital-marketing" element={<DigitalMarketing />} />
            <Route path="/seo" element={<SEOServices />} />
            <Route path="/google-ads" element={<GoogleAds />} />
            <Route path="/social-media-marketing" element={<SocialMediaMarketing />} />
            <Route path="/content-marketing" element={<ContentMarketing />} />
            <Route path="/law-firm-marketing" element={<LawFirmMarketing />} />
            <Route path="/medical-practice-marketing" element={<MedicalPracticeMarketing />} />
            <Route path="/construction-web-design" element={<ConstructionWebDesign />} />
            <Route path="/dental-web-design" element={<DentalWebDesign />} />
            <Route path="/review-request" element={<ReviewRequest />} />
            <Route path="/home-services" element={<HomeServices />} />
            <Route path="/roofing-web-design" element={<RoofingWebDesign />} />
            <Route path="/plumber-web-design" element={<PlumberWebDesign />} />
            <Route path="/hvac-web-design" element={<HvacWebDesign />} />
            <Route path="/electrician-web-design" element={<ElectricianWebDesign />} />
            <Route path="/landscaping-web-design" element={<LandscapingWebDesign />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            {locations.map((loc) => (
              <Route
                key={loc.slug}
                path={`/${loc.slug}`}
                element={<LocationPage location={loc} />}
              />
            ))}
          </Routes>
          </Suspense>
        </main>
        <Footer />
        <ThemeSwitcher />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
